import { useTranslation } from 'react-i18next';
import type { FoundersBlockData } from '@foco/contracts';
import { formatDate } from '../../../shared/lib/format';
import { CopyableValue, useLocale } from './primitives';

/**
 * Founders, owners and management — the "related persons" model of decision 6.
 *
 * A person appears here only in connection with a company: as a founder, an
 * owner, a director or a legal representative. There is no person profile
 * anywhere in the product, and the document number is already masked by the
 * source (Brazilian registries publish `***112108**`), so nothing unmasked can
 * reach this component.
 */
export function FoundersBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const block = (data ?? {}) as FoundersBlockData;
  const persons = block.persons ?? [];

  if (persons.length === 0) {
    return <p className="text-sm text-[var(--color-muted-fg)]">{t('blocks.founders.empty')}</p>;
  }

  return (
    <div>
      <p className="mb-2 text-xs text-[var(--color-muted-fg)]">
        {t('blocks.founders.total', { count: block.total ?? persons.length })}
      </p>

      <ul className="divide-y divide-[var(--color-border)]">
        {persons.map((person, index) => (
          <li key={`${person.nameMasked}-${index}`} className="py-2 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-sm font-medium">
                <CopyableValue value={person.name ?? person.nameMasked} />
              </span>
              {person.role && (
                <span className="rounded-full bg-[var(--color-surface-2)] px-2 py-0.5 text-xs text-[var(--color-muted-fg)]">
                  {person.role}
                </span>
              )}
            </div>

            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[var(--color-muted-fg)]">
              {person.idMasked && (
                <span>
                  {t('blocks.founders.document')}:{' '}
                  <span className="font-mono">{person.idMasked}</span>
                </span>
              )}
              {person.since && (
                <span>
                  {t('blocks.founders.since')}: {formatDate(person.since, locale)}
                </span>
              )}
              {person.sharePercent !== null && person.sharePercent !== undefined && (
                <span>
                  {t('blocks.founders.share')}: {person.sharePercent}%
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

